import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Select, message } from 'antd';
import { 
  DownloadOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import { statsAPI } from '@/services/api/stats';
import type {
  UpgradeStatisticsStat,
  UpgradeTrendStat,
  UpgradeStatusDistributionStat,
  DailyUpgradeDistributionStat,
} from '@/types/api';
import dayjs from 'dayjs';

interface TrendData {
  date: string;
  success: number;
  failure: number;
  total: number;
}

interface StatusData {
  name: string;
  value: number;
  color: string;
  [key: string]: any;
}

interface DailyData {
  date: string;
  count: number;
}

const UpgradeStatistics: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'today' | 'last_7_days' | 'last_30_days' | 'last_90_days'>('today');
  const [loading, setLoading] = useState(false);
  const [upgradeStats, setUpgradeStats] = useState<UpgradeStatisticsStat | null>(null);
  const [trendStat, setTrendStat] = useState<UpgradeTrendStat | null>(null);
  const [statusStat, setStatusStat] = useState<UpgradeStatusDistributionStat | null>(null);
  const [dailyStat, setDailyStat] = useState<DailyUpgradeDistributionStat | null>(null);

  // 获取统计数据
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const params = { time_range: timeRange };
        const [stats, trend, status, daily] = await Promise.all([
          statsAPI.getUpgradeStatistics(params),
          statsAPI.getUpgradeTrend(params),
          statsAPI.getUpgradeStatusDistribution(params),
          statsAPI.getDailyUpgradeDistribution(params),
        ]);
        setUpgradeStats(stats);
        setTrendStat(trend);
        setStatusStat(status);
        setDailyStat(daily);
      } catch (error: any) {
        console.error('获取升级统计数据失败:', error);
        message.error(error?.message || '获取升级统计数据失败');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [timeRange]);

  // 时间范围选项
  const timeRangeOptions = [
    { label: '今天', value: 'today' },
    { label: '最近7天', value: 'last_7_days' },
    { label: '最近30天', value: 'last_30_days' },
    { label: '最近90天', value: 'last_90_days' },
  ];

  // 格式化日期显示（从 ISO 字符串转为 MM/DD）
  const formatDate = (dateStr: string): string => {
    try {
      return dayjs(dateStr).format('MM/DD');
    } catch {
      return dateStr;
    }
  };

  // 趋势图数据
  const trendData: TrendData[] =
    trendStat?.points?.map((p) => ({
      date: formatDate(p.date),
      success: p.success,
      failure: p.failure,
      total: p.total,
    })) || [];

  // 升级状态分布数据
  const statusData: StatusData[] = statusStat
    ? [
        { name: '成功', value: statusStat.successPercent, color: '#52c41a' },
        { name: '待升级', value: statusStat.pendingPercent, color: '#fa8c16' },
        { name: '进行中', value: statusStat.inProgressPercent, color: '#1890ff' },
        { name: '失败', value: statusStat.failedPercent, color: '#f5222d' },
      ]
    : [];

  // 每日升级分布数据
  const dailyData: DailyData[] =
    dailyStat?.points?.map((p) => ({
      date: formatDate(p.date),
      count: p.count,
    })) || [];

  // 自定义Tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-3 border rounded-lg shadow-lg" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
          <p className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // 饼图专用Tooltip
  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div 
          className="p-3 rounded-lg shadow-lg"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            border: '1px solid var(--border-primary)',
            color: 'var(--text-primary)'
          }}
        >
          <p style={{ color: 'var(--text-primary)', margin: 0 }}>
            {data.name}: {data.value}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* 页面标题和时间选择器 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>升级统计</h1>
          <p style={{ color: 'var(--text-secondary)' }}>查看系统升级数据统计和趋势分析</p>
        </div>
        <div className="flex items-center space-x-4">
          <span style={{ color: 'var(--text-secondary)' }}>时间范围:</span>
          <Select
            value={timeRange}
            onChange={setTimeRange}
            style={{ width: 120 }}
          >
            {timeRangeOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>

      {/* 关键指标卡片 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="h-full" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Statistic
                  title={<span style={{ color: 'var(--text-secondary)' }}>总升级次数</span>}
                  value={upgradeStats?.totalUpgradeCount || 0}
                  valueStyle={{ color: 'var(--text-primary)', fontSize: '24px', fontWeight: 'bold' }}
                />
                {upgradeStats?.totalUpgradeCountChange !== undefined && (
                  <div className="flex items-center mt-2">
                    {upgradeStats.totalUpgradeCountChange >= 0 ? (
                      <>
                        <ArrowUpOutlined className="mr-1" style={{ color: 'var(--color-success)' }} />
                        <span className="text-sm" style={{ color: 'var(--color-success)' }}>
                          +{upgradeStats.totalUpgradeCountChange.toFixed(1)}% vs 上周
                        </span>
                      </>
                    ) : (
                      <>
                        <ArrowDownOutlined className="mr-1" style={{ color: 'var(--color-error)' }} />
                        <span className="text-sm" style={{ color: 'var(--color-error)' }}>
                          {upgradeStats.totalUpgradeCountChange.toFixed(1)}% vs 上周
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="ml-4">
                <DownloadOutlined style={{ fontSize: '24px', color: 'var(--color-primary)' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="h-full" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Statistic
                  title={<span style={{ color: 'var(--text-secondary)' }}>成功率</span>}
                  value={upgradeStats?.successRate || 0}
                  suffix="%"
                  precision={1}
                  valueStyle={{ color: 'var(--text-primary)', fontSize: '24px', fontWeight: 'bold' }}
                />
                {upgradeStats?.successRateChange !== undefined && (
                  <div className="flex items-center mt-2">
                    {upgradeStats.successRateChange >= 0 ? (
                      <>
                        <ArrowUpOutlined className="mr-1" style={{ color: 'var(--color-success)' }} />
                        <span className="text-sm" style={{ color: 'var(--color-success)' }}>
                          +{upgradeStats.successRateChange.toFixed(1)}% vs 上周
                        </span>
                      </>
                    ) : (
                      <>
                        <ArrowDownOutlined className="mr-1" style={{ color: 'var(--color-error)' }} />
                        <span className="text-sm" style={{ color: 'var(--color-error)' }}>
                          {upgradeStats.successRateChange.toFixed(1)}% vs 上周
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="ml-4">
                <CheckCircleOutlined style={{ fontSize: '24px', color: 'var(--color-success)' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="h-full" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Statistic
                  title={<span style={{ color: 'var(--text-secondary)' }}>失败率</span>}
                  value={upgradeStats?.failureRate || 0}
                  suffix="%"
                  precision={1}
                  valueStyle={{ color: 'var(--text-primary)', fontSize: '24px', fontWeight: 'bold' }}
                />
                {upgradeStats?.failureRateChange !== undefined && (
                  <div className="flex items-center mt-2">
                    {upgradeStats.failureRateChange >= 0 ? (
                      <>
                        <ArrowUpOutlined className="mr-1" style={{ color: 'var(--color-error)' }} />
                        <span className="text-sm" style={{ color: 'var(--color-error)' }}>
                          +{upgradeStats.failureRateChange.toFixed(1)}% vs 上周
                        </span>
                      </>
                    ) : (
                      <>
                        <ArrowDownOutlined className="mr-1" style={{ color: 'var(--color-success)' }} />
                        <span className="text-sm" style={{ color: 'var(--color-success)' }}>
                          {upgradeStats.failureRateChange.toFixed(1)}% vs 上周
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="ml-4">
                <CloseCircleOutlined style={{ fontSize: '24px', color: 'var(--color-error)' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="h-full" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Statistic
                  title={<span style={{ color: 'var(--text-secondary)' }}>平均耗时</span>}
                  value={upgradeStats?.averageTimeSpent || 0}
                  suffix="分钟"
                  precision={1}
                  valueStyle={{ color: 'var(--text-primary)', fontSize: '24px', fontWeight: 'bold' }}
                />
                {upgradeStats?.averageTimeSpentChange !== undefined && (
                  <div className="flex items-center mt-2">
                    {upgradeStats.averageTimeSpentChange >= 0 ? (
                      <>
                        <ArrowUpOutlined className="mr-1" style={{ color: 'var(--color-error)' }} />
                        <span className="text-sm" style={{ color: 'var(--color-error)' }}>
                          +{upgradeStats.averageTimeSpentChange.toFixed(1)}分钟 vs 上周
                        </span>
                      </>
                    ) : (
                      <>
                        <ArrowDownOutlined className="mr-1" style={{ color: 'var(--color-success)' }} />
                        <span className="text-sm" style={{ color: 'var(--color-success)' }}>
                          {upgradeStats.averageTimeSpentChange.toFixed(1)}分钟 vs 上周
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="ml-4">
                <ClockCircleOutlined style={{ fontSize: '24px', color: 'var(--color-warning)' }} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 升级趋势图表 */}
      <Card 
        title="升级趋势" 
        loading={loading}
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
        styles={{
          header: {
            background: 'var(--bg-tertiary)',
            borderBottom: '1px solid var(--border-primary)',
            color: 'var(--text-primary)'
          }
        }}
      >
        <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
          {timeRange === 'today' ? '今天的' : timeRange === 'last_7_days' ? '最近7天的' : timeRange === 'last_30_days' ? '最近30天的' : '最近90天的'}升级成功率和失败率趋势
        </p>
        <div style={{ height: '300px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-secondary)" />
              <XAxis 
                dataKey="date" 
                stroke="var(--text-secondary)"
                fontSize={12}
              />
              <YAxis 
                stroke="var(--text-secondary)"
                fontSize={12}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="failure" 
                stroke="#f5222d" 
                strokeWidth={2}
                dot={{ fill: '#f5222d', strokeWidth: 2, r: 4 }}
                name="失败"
              />
              <Line 
                type="monotone" 
                dataKey="total" 
                stroke="#1890ff" 
                strokeWidth={2}
                dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                name="总数"
              />
              <Line 
                type="monotone" 
                dataKey="success" 
                stroke="#52c41a" 
                strokeWidth={2}
                dot={{ fill: '#52c41a', strokeWidth: 2, r: 4 }}
                name="成功"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* 底部图表区域 */}
      <Row gutter={[16, 16]}>
        {/* 升级状态分布 */}
        <Col xs={24} lg={12}>
          <Card 
            title="升级状态分布" 
            loading={loading}
            className="h-full"
            style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
            styles={{
              header: {
                background: 'var(--bg-tertiary)',
                borderBottom: '1px solid var(--border-primary)',
                color: 'var(--text-primary)'
              }
            }}
          >
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>当前所有设备的升级状态</p>
            {statusData.length > 0 ? (
              <>
                <div style={{ height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        nameKey="name"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<PieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                {/* 图例 */}
                <div className="mt-4 space-y-2">
                  {statusData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{item.name}</span>
                      </div>
                      <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {item.value.toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                暂无数据
              </div>
            )}
          </Card>
        </Col>

        {/* 每日升级分布 */}
        <Col xs={24} lg={12}>
          <Card 
            title="每日升级分布" 
            loading={loading}
            className="h-full"
            style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
            styles={{
              header: {
                background: 'var(--bg-tertiary)',
                borderBottom: '1px solid var(--border-primary)',
                color: 'var(--text-primary)'
              }
            }}
          >
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              {timeRange === 'today' ? '今天的' : timeRange === 'last_7_days' ? '最近7天的' : timeRange === 'last_30_days' ? '最近30天的' : '最近90天的'}每日升级次数分布
            </p>
            {dailyData.length > 0 ? (
              <div style={{ height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-secondary)" />
                    <XAxis 
                      dataKey="date" 
                      stroke="var(--text-secondary)"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="var(--text-secondary)"
                      fontSize={12}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                      contentStyle={{
                        backgroundColor: 'var(--bg-tertiary)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '6px',
                        color: 'var(--text-primary)'
                      }}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="#1890ff"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                暂无数据
              </div>
            )}
            
            {/* 统计信息 */}
            <div className="mt-8 grid grid-cols-3 gap-4">
              <div className="p-3 rounded-lg text-center" style={{ background: 'var(--bg-tertiary)' }}>
                <div className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>总升级次数</div>
                <div className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                  {dailyStat?.totalCount?.toLocaleString() || 0}
                </div>
              </div>
              <div className="p-3 rounded-lg text-center" style={{ background: 'var(--bg-tertiary)' }}>
                <div className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>平均每日</div>
                <div className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                  {dailyStat?.averageDaily ? Math.round(dailyStat.averageDaily) : 0}
                </div>
              </div>
              <div className="p-3 rounded-lg text-center" style={{ background: 'var(--bg-tertiary)' }}>
                <div className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>峰值</div>
                <div className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                  {dailyStat?.peakValue?.toLocaleString() || 0}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UpgradeStatistics;
